import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForum } from '../useForum';
import { FolderOpen, RefreshCw, Search, WifiOff } from 'lucide-react';
import { CreateSubforumForm } from './CreateSubforumForm';
import { useAuth } from '@/app/hooks/useAuth';
import { displaySpaceName } from "../forum-display";

/**
 * The /forum index, in the dcv2 design language.
 *
 * The three rotating accents the previous design gave each card are gone: the
 * canvas has one accent, and cycling three hues across a list encoded no
 * information — the position in the array. What the position actually is now
 * reads as the mono numeral in the card's corner, which is how the canvas's
 * capability grid indexes itself.
 *
 * ForumCardSkeleton is no longer used here. It is shared with /forum/archive,
 * /forum/:slug and ThreadList, which are still on the old shell, so restyling
 * it would have restyled three pages that were not in scope. The loading state
 * below draws its own bars instead.
 */

// Vertical padding only. The side gutter is `.site-gutter` on each section, so
// it can narrow on a phone (global.css, T101026002).
const SECTION: React.CSSProperties = {
  paddingTop: 118,
  paddingBottom: 118,
  position: 'relative',
  // X ONLY. `dc-h-card:hover` lifts a card by translateY(-5px) and draws a
  // 40px-blur shadow beneath it; the card grid below renders in a section with
  // `paddingTop: 0`, so the lifted card's top edge leaves the content box and a
  // plain `overflow: clip` sliced it off mid-hover.
  //
  // `overflow-x: clip` is the right tool rather than `hidden`: `hidden` on one
  // axis forces the other to compute as `auto`, which would reintroduce
  // clipping (and a scroll container). `clip` leaves `overflow-y: visible`
  // genuinely visible, so horizontal bleed is still contained — which is all
  // this was ever guarding against.
  overflowX: 'clip',
};

const CONTAINER: React.CSSProperties = { maxWidth: 1240, margin: '0 auto', position: 'relative' };

// `min(100%, 300px)`, not a bare 300px: a phone column narrower than 300px
// would otherwise hold a card wider than itself, cut off at the clipped edge.
// Anything 300px or wider resolves exactly as before (T101026002).
const CARD_GRID = 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))';

const EYEBROW: React.CSSProperties = {
  fontFamily: "'Manrope', sans-serif",
  fontSize: 11.5,
  letterSpacing: '.16em',
  textTransform: 'uppercase',
  color: 'var(--link)',
  marginBottom: 18,
};

const H2: React.CSSProperties = {
  margin: '0 0 16px',
  fontFamily: "'Manrope', sans-serif",
  fontWeight: 600,
  fontSize: 'clamp(28px, 3.4vw, 46px)',
  letterSpacing: '-0.02em',
  lineHeight: 1.14,
};

const CARD: React.CSSProperties = {
  background: 'var(--paper2)',
  border: '1px solid rgba(var(--ink-rgb),.13)',
  borderRadius: 18,
  padding: 28,
};

const PRIMARY_BTN: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 10,
  cursor: 'pointer',
  background: 'var(--btn)',
  color: 'var(--btnfg)',
  border: 'none',
  borderRadius: 999,
  padding: '11px 24px',
  fontFamily: "'Manrope', sans-serif",
  fontWeight: 700,
  fontSize: 14,
  transition: 'transform .2s, box-shadow .2s',
};

const GHOST_BTN: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 10,
  cursor: 'pointer',
  background: 'transparent',
  border: '1px solid rgba(var(--ink-rgb),.3)',
  borderRadius: 999,
  padding: '11px 24px',
  fontFamily: "'Manrope', sans-serif",
  fontWeight: 600,
  fontSize: 14,
  color: 'rgba(var(--ink-rgb),.66)',
  transition: 'border-color .2s, background .2s, color .2s',
};

const ICON_TILE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 52,
  height: 52,
  borderRadius: 9,
  background: 'var(--chip)',
  color: '#9DF1FF',
  marginBottom: 22,
};

function Bar({ w, h = 12, r = 6, d = 0 }: { w: number | string; h?: number; r?: number; d?: number }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'block',
        width: w,
        height: h,
        borderRadius: r,
        background: 'rgba(var(--ink-rgb),.09)',
        animation: `pulseDot 1.8s ${d}s ease-in-out infinite`,
      }}
    />
  );
}

/** The shell the empty and no-match states share. */
function Notice({
  icon,
  title,
  body,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  body?: string;
  action?: React.ReactNode;
}) {
  return (
    <div style={{ ...CARD, padding: '64px 28px', textAlign: 'center' }}>
      <span aria-hidden="true" style={ICON_TILE}>
        {icon}
      </span>
      <h2
        style={{
          margin: '0 0 8px',
          fontFamily: "'Manrope', sans-serif",
          fontWeight: 600,
          fontSize: 18,
          letterSpacing: '-0.01em',
        }}
      >
        {title}
      </h2>
      {body && (
        <p style={{ margin: '0 auto 26px', maxWidth: 420, fontSize: 14.5, color: 'rgba(var(--ink-rgb),.66)' }}>
          {body}
        </p>
      )}
      {action}
    </div>
  );
}

export function SubforumList() {
  const { state, fetchSubforums } = useForum();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  if (state.loading.subforums) {
    return (
      <>
        <section className="site-gutter" style={{ ...SECTION, paddingBottom: 56 }}>
          <div style={CONTAINER}>
            <Bar w={170} h={13} />
            <div style={{ height: 22 }} />
            <Bar w="min(620px, 100%)" h={52} r={12} d={0.05} />
            <div style={{ height: 18 }} />
            <Bar w="min(520px, 100%)" h={16} d={0.1} />
            <div style={{ height: 30 }} />
            <Bar w="min(420px, 100%)" h={44} r={999} d={0.15} />
          </div>
        </section>
        <section className="site-gutter" style={{ ...SECTION, paddingTop: 0 }}>
          <div
            style={{
              ...CONTAINER,
              display: 'grid',
              gridTemplateColumns: CARD_GRID,
              gap: 14,
            }}
          >
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} style={CARD}>
                <Bar w={28} h={11} d={i * 0.05} />
                <div style={{ height: 22 }} />
                <Bar w="70%" h={17} d={i * 0.05 + 0.05} />
                <div style={{ height: 14 }} />
                <Bar w="100%" d={i * 0.05 + 0.1} />
                <div style={{ height: 7 }} />
                <Bar w="78%" d={i * 0.05 + 0.15} />
                <div style={{ height: 24 }} />
                <Bar w={130} h={28} r={999} d={i * 0.05 + 0.2} />
              </div>
            ))}
          </div>
        </section>
      </>
    );
  }

  if (state.errors.subforums) {
    return (
      <section className="site-gutter" style={{ ...SECTION, paddingTop: 150, paddingBottom: 130, textAlign: 'center' }}>
        <div style={{ maxWidth: 620, margin: '0 auto', position: 'relative' }}>
          <span aria-hidden="true" style={{ ...ICON_TILE, width: 56, height: 56, marginBottom: 26 }}>
            <WifiOff size={24} />
          </span>
          <h1 style={H2}>Unable to load discussions</h1>
          <p style={{ margin: '0 auto 32px', maxWidth: 460, fontSize: 16, color: 'rgba(var(--ink-rgb),.66)' }}>
            We couldn't connect to the server right now. This is usually temporary. Check your connection and try again.
          </p>
          <button type="button" onClick={() => fetchSubforums()} className="dc-h-btn-lg" style={PRIMARY_BTN}>
            <RefreshCw size={16} />
            Try Again
          </button>
        </div>
      </section>
    );
  }

  const filtered = state.subforums.filter(
    (s) =>
      !searchQuery ||
      s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      {/* HERO */}
      <section className="site-gutter" style={{ ...SECTION, paddingBottom: 56 }}>
        <div data-rv="" style={CONTAINER}>
          <div style={EYEBROW}><span className="dc-mark" aria-hidden="true" /> Community Forum</div>
          <h1 style={{ ...H2, maxWidth: 820 }}>
            Build alongside the <span style={{ color: 'var(--acc2)' }}>AI2Aim community.</span>
          </h1>
          <p style={{ margin: '0 0 34px', maxWidth: 640, fontSize: 16, color: 'rgba(var(--ink-rgb),.66)' }}>
            Discussions, technical deep dives, and product feedback from operators, engineers, and partners shipping with our platform.
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              type="search"
              aria-label="Search sub-forums"
              placeholder="Search sub-forums&hellip;"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                flex: '1 1 280px',
                maxWidth: 420,
                background: 'var(--paper2)',
                border: '1px solid rgba(var(--ink-rgb),.16)',
                borderRadius: 999,
                padding: '10px 18px',
                fontFamily: "'Manrope', sans-serif",
                fontSize: 14,
                color: 'var(--ink)',
                outline: 'none',
              }}
            />
            {!isAuthenticated && (
              <button type="button" onClick={() => navigate('/auth')} className="dc-h-ghost" style={GHOST_BTN}>
                Login
              </button>
            )}
            {/* Only where the tenant has opted in. This button used to render
                for everyone, and for most visitors it opened a form whose
                submit could only fail — the write it made needed a session.
                An entry point that leads to a 404 is worse than no entry
                point, so the flag on the subforum read decides. */}
            {state.allowsSubforumRequests && (
              <button
                type="button"
                onClick={() => setShowCreateForm(true)}
                className="dc-h-btn-sm"
                style={PRIMARY_BTN}
              >
                <span aria-hidden="true">+</span>
                Suggest a Category
              </button>
            )}
          </div>
        </div>
      </section>

      {/* SUB-FORUMS */}
      <section className="site-gutter" style={{ ...SECTION, paddingTop: 0 }}>
        <div style={CONTAINER}>
          {showCreateForm && <CreateSubforumForm onClose={() => setShowCreateForm(false)} />}

          {state.subforums.length === 0 ? (
            /* The second door to the same form, and gated on the same flag.
               Missing it would have left the exact button the hero one was
               hidden to avoid — and on the emptiest page, where it is the only
               thing to click. Without the opt-in the empty forum is simply
               reported as empty, with nothing to act on. */
            <Notice
              icon={<FolderOpen size={22} />}
              title="No subforums yet"
              body={
                state.allowsSubforumRequests
                  ? 'The forum is currently empty. Suggest the first category — moderators review each one before it appears.'
                  : 'The forum is currently empty. Check back soon.'
              }
              action={
                state.allowsSubforumRequests ? (
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(true)}
                    className="dc-h-btn-sm"
                    style={PRIMARY_BTN}
                  >
                    <span aria-hidden="true">+</span>
                    Suggest the First Category
                  </button>
                ) : undefined
              }
            />
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: CARD_GRID,
                gap: 14,
              }}
            >
              {filtered.map((subforum, index) => (
                <Link
                  key={subforum.id}
                  to={`/forum/${subforum.slug}`}
                  className="dc-h-card"
                  style={{
                    ...CARD,
                    color: 'inherit',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 22,
                    minHeight: 216,
                    transition: 'transform .25s, box-shadow .25s, border-color .25s',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span
                      style={{
                        fontFamily: "'Manrope', sans-serif",
                        fontSize: 12,
                        color: 'var(--link)',
                      }}
                    >
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: 'var(--acc2)',
                        animation: `dcv2-pulseDot ${2.4 + (index % 5) * 0.2}s ${(index % 5) * 0.2}s ease-in-out infinite`,
                      }}
                    />
                  </div>

                  <div style={{ flex: 1 }}>
                    <h3
                      style={{
                        margin: '0 0 8px',
                        fontFamily: "'Manrope', sans-serif",
                        fontWeight: 600,
                        fontSize: 18,
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {displaySpaceName(subforum.name)}
                    </h3>
                    <p style={{ margin: 0, fontSize: 14.5, color: 'rgba(var(--ink-rgb),.66)' }}>
                      {subforum.description}
                    </p>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      paddingTop: 18,
                      borderTop: '1px solid rgba(var(--ink-rgb),.2)',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "'Manrope', sans-serif",
                        fontSize: 12,
                        color: 'rgba(var(--ink-rgb),.62)',
                      }}
                    >
                      {subforum.postCount} discussion{subforum.postCount !== 1 ? 's' : ''}
                    </span>
                    <span
                      style={{
                        fontFamily: "'Manrope', sans-serif",
                        fontSize: 12,
                        lineHeight: 1.2,
                        borderRadius: 999,
                        padding: '7px 15px',
                        border: '1px solid rgba(var(--ink-rgb),.3)',
                        color: 'var(--link)',
                      }}
                    >
                      Active
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {filtered.length === 0 && state.subforums.length > 0 && (
            <Notice icon={<Search size={22} />} title="No subforums match your search." />
          )}

          {state.subforums.length > 0 && (
            <div style={{ marginTop: 52, textAlign: 'center' }}>
              <Link
                to="/forum/archive"
                className="dc-h-link"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 10,
                  fontFamily: "'Manrope', sans-serif",
                  fontSize: 12,
                  letterSpacing: '.16em',
                  textTransform: 'uppercase',
                  color: 'rgba(var(--ink-rgb),.62)',
                  transition: 'color .2s',
                }}
              >
                Browse Full Archive
                <span aria-hidden="true">&rarr;</span>
              </Link>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
